import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Lock, ArrowRight, AlertCircle, ArrowLeft } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import Logo from "../components/Logo";
import Sparkle from "../components/Sparkle";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/admin";

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(username, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-shay-navy text-shay-cream grid place-items-center px-6 py-16 relative overflow-hidden" data-testid="admin-login-page">
      {/* Sparkles */}
      <Sparkle size={32} color="#C89A36" className="absolute top-[12%] left-[10%] opacity-70 animate-float-slow" />
      <Sparkle size={18} color="#C89A36" className="absolute top-[20%] right-[14%] opacity-60" />
      <Sparkle size={26} color="#C89A36" className="absolute bottom-[18%] left-[16%] opacity-60 animate-float-slow" />
      <Sparkle size={22} color="#C89A36" className="absolute bottom-[24%] right-[12%] opacity-50" />

      <Link
        to="/"
        className="absolute top-6 left-6 inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-shay-cream/70 hover:text-shay-gold transition-colors"
        data-testid="admin-login-home-link"
      >
        <ArrowLeft size={14} /> Back to Home
      </Link>

      <div className="w-full max-w-md relative">
        <div className="flex justify-center mb-8">
          <Logo variant="plain" size="lg" />
        </div>
        <div className="text-center mb-10">
          <p className="eyebrow eyebrow-gold flex items-center justify-center gap-2">
            <Sparkle size={10} color="#C89A36" /> Admin Access
          </p>
          <h1 className="font-display text-4xl md:text-5xl mt-3 tracking-tight">
            Welcome back, <span className="italic text-shay-gold">Shay.</span>
          </h1>
          <p className="mt-4 text-shay-cream/65 text-sm">Sign in to manage orders and your menu.</p>
        </div>

        <form onSubmit={submit} className="space-y-7" data-testid="admin-login-form">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-shay-gold/80">Username</label>
            <input
              required
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="admin"
              className="w-full bg-transparent border-0 border-b border-shay-cream/30 px-0 py-3 mt-1 text-shay-cream placeholder:text-shay-cream/30 focus:outline-none focus:border-shay-gold transition-colors"
              data-testid="admin-username-input"
              autoComplete="username"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-shay-gold/80">Password</label>
            <input
              required
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full bg-transparent border-0 border-b border-shay-cream/30 px-0 py-3 mt-1 text-shay-cream placeholder:text-shay-cream/30 focus:outline-none focus:border-shay-gold transition-colors tracking-widest"
              data-testid="admin-password-input"
              autoComplete="current-password"
            />
          </div>

          {error && (
            <div className="flex items-start gap-2.5 text-sm text-shay-cream bg-shay-gold/10 border border-shay-gold/30 px-4 py-3 rounded-sm" data-testid="admin-login-error">
              <AlertCircle size={16} className="text-shay-gold shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="btn-gold w-full disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="admin-login-submit"
          >
            {submitting ? "Signing in..." : (
              <>Sign In <ArrowRight size={16} /></>
            )}
          </button>
        </form>

        <p className="mt-10 text-center text-[10px] uppercase tracking-widest text-shay-cream/40 flex items-center justify-center gap-2">
          <Lock size={11} /> Secure Admin Area · Authorized Personnel Only
        </p>
      </div>
    </div>
  );
}
